'use client'

import { tw } from '@/lib/utils'

import { useTooltipBubble } from './use-tooltip-bubble'

interface InfoTooltipProps {
  title: string
  text: string
  align?: 'center' | 'right'
  // Positioning tweaks from the call site (e.g. `ml-1.5 translate-y-[2px]` to
  // flow inline after the last word of a label).
  className?: string
}

const InfoIcon = () => (
  <svg aria-hidden className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" />
  </svg>
)

export const InfoTooltip = ({ title, text, align = 'center', className }: InfoTooltipProps) => {
  const { open, setOpen, ref, bubbleRef, alignClass } = useTooltipBubble(align)

  return (
    <span
      ref={ref}
      onPointerEnter={(event) => {
        if (event.pointerType === 'mouse') {
          setOpen(true)
        }
      }}
      onPointerLeave={(event) => {
        if (event.pointerType === 'mouse') {
          setOpen(false)
        }
      }}
      className={tw('group relative inline-flex', className)}
    >
      <button
        type="button"
        data-tracking="ignore"
        aria-label={title}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="text-navy/35 hover:text-navy/70 inline-flex cursor-help transition-colors"
      >
        <InfoIcon />
      </button>
      <span
        ref={bubbleRef}
        role="tooltip"
        className={`bg-navy pointer-events-none absolute bottom-full z-50 mb-2 w-64 rounded-xl px-4 py-3 text-left text-xs leading-relaxed font-normal tracking-normal whitespace-normal text-white normal-case shadow-lg transition-opacity ${alignClass} ${open ? 'pointer-events-auto opacity-100' : 'hidden'}`}
      >
        <span className="mb-1 block text-xs font-bold">{title}</span>
        {text}
      </span>
    </span>
  )
}
